import { useState, type ReactNode } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, type MeResponse } from '../../../api/client'
import { deskPubsOf, deskTabsOf, firstAllowed } from '../../../lib/deskAccess'
import { Panel } from '../../../components/Panel'
import { deskApi, type DeskPub } from '../../../api/visitorsDesk'
import {
  COMPARE_OPTIONS, DESK_GROUPS, deskDates, deskQueryToSearch, periodOptions,
  type DeskCompare, type DeskGroup, type DeskPeriodKind, type DeskQuery, type DeskTab,
} from '../../../lib/deskPeriod'
import { filterLabel, sessionsLink, unappliedFilters, type DeskFilter } from '../../../lib/deskFilters'
import { DESK_TABS, deskPath, frameIncluded, isDeskPub, tabAvailable } from '../../../lib/deskTabs'
import { botChipText, fmtDay, freshnessText, includedText, pubLabel } from '../../../lib/deskFormat'
import { statusApproxNote } from '../../../lib/deskGroupNote'
import { todayEastern } from '../../../lib/visitorsRange'
import { useDeskState } from './useDeskState'
import { DeskBotPanel } from './DeskBotPanel'
import { NotYetLive } from './NotYetLive'
import { TAB_HELP, manualHref } from './manual'

const sel = 'min-h-[44px] md:min-h-[32px] bg-ink-900 border border-ink-500 px-2 font-mono text-[12px] text-paper'
const lbl = 'flex flex-col gap-1'

/** The search string with period, compare and custom dates replaced by those of `q`; filters and other keys stay. */
function withQuery(spKey: string, q: DeskQuery): URLSearchParams {
  const next = new URLSearchParams(spKey)
  for (const k of ['period', 'from', 'to', 'compare', 'g']) next.delete(k)
  for (const [k, v] of Object.entries(deskQueryToSearch(q))) next.set(k, v)
  return next
}

/** Shared chrome of every desk tab: title/period/compare pickers, the bot chip,
 *  tabs, filter pills, the data-through note, and the availability gate. */
export function DeskFrame({ tab, children }: { tab: DeskTab; children: ReactNode }) {
  const { pub, spKey, setSp, q, filters } = useDeskState(tab)
  const [botOpen, setBotOpen] = useState(false)
  const valid = isDeskPub(pub)
  const today = todayEastern()

  const meQ = useQuery<MeResponse>({ queryKey: ['me'], queryFn: () => api.get<MeResponse>('/api/me'), staleTime: 60_000, retry: false })
  const me = meQ.data
  const tabs = deskTabsOf(me)
  const pubs = deskPubsOf(me)
  // /desk/bots is admin-only: an account without the Today tab never asks for it.
  const full = tabs.includes('today')
  const refused = !!me && (!pubs.includes(pub as DeskPub) || !tabs.includes(tab))

  const avail = useQuery({ queryKey: ['desk', 'availability'], queryFn: deskApi.availability, enabled: valid && !!me && !refused, staleTime: 60_000, retry: false })
  const bots = useQuery({
    queryKey: ['desk', 'bots', pub, q.period, q.from, q.to, q.compare, q.group],
    queryFn: () => deskApi.bots(pub as DeskPub, q),
    enabled: valid && full && !refused, retry: false,
  })

  const wrap = 'space-y-6 max-w-[1500px]'
  if (!valid) {
    return (
      <div className={wrap}>
        <Panel accent="red">
          <p className="text-paper">Unknown title</p>
          <Link to={deskPath('all', 'today')} className="text-amber-glow hover:underline font-mono text-[12px]">Back to all titles</Link>
        </Panel>
      </div>
    )
  }

  if (refused) {
    const to = firstAllowed(me, pub, tab)
    return to ? <Navigate to={`${to}${spKey ? `?${spKey}` : ''}`} replace /> : <Navigate to="/" replace />
  }

  const gate = avail.isError ? tabAvailable(tab, pub, null) : avail.data ? tabAvailable(tab, pub, avail.data.titles) : null
  const fresh = avail.data ? (pub === 'all' ? avail.data.all : avail.data.titles.find((t) => t.publication === pub)?.freshness) : undefined
  const b = bots.data
  const meta = b?.meta
  const period = meta?.period ?? null
  const hidden = b && b.unavailable === undefined ? b.hidden : null
  const inc = frameIncluded(tab, meta, avail.data?.titles)

  const go = (next: DeskQuery) => setSp(withQuery(spKey, next))
  const onPeriod = (p: DeskPeriodKind) => {
    if (p === 'custom') {
      const d = deskDates(q, today)
      go({ ...q, period: 'custom', from: d.from, to: d.to })
    } else go({ period: p, compare: q.compare, group: q.group })
  }
  const removeFilter = (f: DeskFilter) => {
    const next = new URLSearchParams(spKey)
    next.delete(`f.${f.key}`)
    setSp(next)
  }
  const unapplied = unappliedFilters(tab, filters)
  const visits = full ? sessionsLink(pub, filters, q, today) : null
  const dates = deskDates(q, today)

  return (
    <div className={wrap}>
      <header className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap items-end gap-4">
            <h1 className="sr-only">Visitors Desk — {pubLabel(pub)}</h1>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className={lbl}>
              <span className="label">Period</span>
              <select aria-label="Period" className={sel} value={q.period} onChange={(e) => onPeriod(e.target.value as DeskPeriodKind)}>
                {periodOptions(tab).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            {q.period === 'custom' && (
              <>
                <label className={lbl}>
                  <span className="label">From</span>
                  <input type="date" aria-label="From" className={sel} value={q.from ?? dates.from} max={q.to}
                         onChange={(e) => e.target.value && e.target.value <= (q.to ?? dates.to) && go({ ...q, from: e.target.value })} />
                </label>
                <label className={lbl}>
                  <span className="label">To</span>
                  <input type="date" aria-label="To" className={sel} value={q.to ?? dates.to} min={q.from}
                         onChange={(e) => e.target.value && e.target.value >= (q.from ?? dates.from) && go({ ...q, to: e.target.value })} />
                </label>
              </>
            )}
            <label className={lbl}>
              <span className="label">Compare</span>
              <select aria-label="Compare" className={sel} value={q.compare} onChange={(e) => go({ ...q, compare: e.target.value as DeskCompare })}>
                {COMPARE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <div role="group" aria-label="Group" className={lbl}>
              <span className="label">Who</span>
              <div className="flex max-w-full flex-wrap border border-ink-500">
                {DESK_GROUPS.map((g) => {
                  const on = (q.group ?? 'all') === g.key
                  return (
                    <button key={g.key} type="button" aria-pressed={on} onClick={() => go({ ...q, group: g.key as DeskGroup })}
                            className={`min-h-[44px] md:min-h-[32px] px-2.5 font-mono text-[12px] ${on ? 'bg-amber-glow text-ink-900' : 'bg-ink-900 text-paper-dim hover:text-paper hover:bg-ink-700'}`}>
                      {g.label}
                    </button>
                  )
                })}
              </div>
            </div>
            {full ? (
              <button
                type="button"
                onClick={() => setBotOpen(true)}
                className="min-h-[44px] md:min-h-[32px] px-3 font-mono text-[12px] border border-ink-500 text-paper-dim hover:text-paper hover:bg-ink-700"
                aria-haspopup="dialog"
              >
                {botChipText(hidden)}
              </button>
            ) : (
              <span className="inline-flex min-h-[44px] md:min-h-[32px] items-center px-3 font-mono text-[12px] border border-ink-500 text-paper-dim">
                Humans only · bots removed
              </span>
            )}
          </div>
        </div>

        <nav aria-label="Desk sections" className="flex overflow-x-auto border-b border-ink-500 -mx-4 px-4 md:mx-0 md:px-0">
          {DESK_TABS.filter((t) => tabs.includes(t.id)).map((t) => (
            <Link
              key={t.id}
              to={deskPath(pub, t.id, spKey)}
              aria-current={t.id === tab ? 'page' : undefined}
              className={`shrink-0 min-h-[44px] px-3.5 inline-flex items-center border-b-2 -mb-px font-mono text-[12px] ${
                t.id === tab ? 'border-amber-glow text-paper' : 'border-transparent text-paper-dim hover:text-paper'
              }`}
            >
              {t.label}
            </Link>
          ))}
          <Link to={manualHref(TAB_HELP[tab])} className="ml-auto shrink-0 min-h-[44px] pl-3.5 inline-flex items-center text-[13px] font-semibold text-amber-glow underline underline-offset-2 hover:text-paper">
            How this works
          </Link>
        </nav>

        {filters.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" aria-label="Filters">
            {filters.map((f) => (
              <span key={f.key} className="inline-flex items-center gap-2 border border-ink-500 bg-ink-800 pl-3 font-mono text-[12px] text-paper">
                {filterLabel(f)}
                {unapplied.includes(f) && <span className="text-paper-faint">Not filtered by {filterLabel(f).split(':')[0]}</span>}
                <button type="button" aria-label={`Remove filter ${filterLabel(f)}`} onClick={() => removeFilter(f)}
                        className="min-h-[32px] min-w-[32px] text-paper-dim hover:text-paper">×</button>
              </span>
            ))}
            {visits && <Link to={visits} className="font-mono text-[12px] text-amber-glow hover:underline">See the visits →</Link>}
          </div>
        )}

        {fresh && <p className="font-mono text-[11px] text-paper-faint">{freshnessText(fresh)}</p>}
        {statusApproxNote(q.group, meta) && <p className="font-mono text-[11px] text-paper-faint">{statusApproxNote(q.group, meta)}</p>}

        {tab !== 'revenue' && inc && (
          <div className="space-y-1 font-mono text-[11px] text-paper-faint">
            {inc.rollups && period?.incomplete && meta?.through && <p>Rollups through {fmtDay(meta.through)}</p>}
            {inc.rollups && period?.kind === 'mtd' && period.incomplete && !period.live && <p>This month isn’t finished: compared with the same {period.days} days last month</p>}
            {pub === 'all' && inc.titles_excluded.length > 0 && <p>{includedText(inc)}</p>}
          </div>
        )}
      </header>

      {!gate ? <div className="h-32 bg-ink-800 animate-pulse" /> : !gate.ok ? <NotYetLive text={gate.text ?? ''} /> : children}

      {full && botOpen && <DeskBotPanel pub={pub} q={q} bots={bots} onClose={() => setBotOpen(false)} />}
    </div>
  )
}

